import AddIcon from '@mui/icons-material/Add';
import React, { useState, useEffect, useRef } from 'react';

import { BadRequestError } from '_lib/api/apiErrors';
import { Contact, ContactDTO, ST } from '_lib/api/apiTypes.shim';
import { ContactChildResource } from '_lib/api/stApiResourceFactory';

import { Box } from '_lib/components/Box/Box';
import { Chip } from '_lib/components/Chip/Chip';

import { formatPhone } from '_lib/utils/helpers';
import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { ReadOnlyContact } from '../../../../featureComponents/ReadOnlyContact/ReadOnlyContact';

import { EditableContact } from './EditableContact';

export interface ContactsProps {
  contacts: Contact[];
  contactsResource: ContactChildResource;
  onContactsUpdated: (contacts: Contact[]) => void;
  disabledFromParent?: boolean;
}

export const Contacts = (props: ContactsProps): React.ReactElement => {
  const { contacts, contactsResource, onContactsUpdated, disabledFromParent } = props;

  const { enqueueSnackbar, enqueueUndoSnackbar } = useOurSnackbar();

  const [contactBeingEdited, setContactBeingEdited] = useState<Contact | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const [isAddingContact, setIsAddingContact] = useState(false);
  const [contactBeingAdded, setContactBeingAdded] = useState<ContactDTO>({
    type: ST.ContactTypeEnum.MobilePhone,
    value: ''
  });

  /**
   * Used to ensure UNDO deletion always runs against the current version of contacts
   */
  const contactsRef = useRef<Contact[]>(contacts);

  useEffect(() => {
    contactsRef.current = contacts;
    setContactBeingEdited(undefined); // any time contacts change, clear out editing state
  }, [contacts]);

  const revertContactDeletion = (contact: Contact) => {
    onContactsUpdated([...contactsRef.current, contact]);
  };

  /**
   * Delete a contact. Note that we only use this in the readOnly contact, we do not want to allow deleting a
   * contact while it is being edited.
   * @param contact contact we want to delete
   */
  const handleDeleteContact = async (contact: Contact) => {
    if (!contact.providerId) return;

    setIsSaving(true);
    try {
      const contactsAfterRemoved = contacts.filter((c) => c.providerId !== contact.providerId);
      onContactsUpdated(contactsAfterRemoved);

      const isEmail = contact.type === ST.ContactTypeEnum.Email;
      const snackMessage = `Deleted contact: ${isEmail ? contact.value : formatPhone(contact.value)}`;

      enqueueUndoSnackbar(snackMessage, {
        onUndo: () => revertContactDeletion(contact),
        onClose: () => contactsResource.deleteItem(contact.providerId).catch(reportException)
      });
    } catch (err) {
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Add the contact that is being created, and add it to the contacts list.
   * Will also pass API errors back to the caller.
   *
   * Note that we need to add this to the copied company list!
   */
  const handleAddContact = async (contact: ContactDTO, setAPIErrors: (err: BadRequestError) => void) => {
    setIsSaving(true);
    try {
      const createdContact = await contactsResource.create(contact);
      setIsAddingContact(false);
      setContactBeingAdded({ type: ST.ContactTypeEnum.MobilePhone, value: '' });
      onContactsUpdated([...contacts, createdContact]);
      enqueueSnackbar('Contact added.');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setAPIErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Save the contact. Will also pass API errors back to the caller.
   * @param contact updated contact
   */
  async function handleSaveContact(contact: Contact, setAPIErrors: (err: BadRequestError) => void) {
    setIsSaving(true);
    try {
      const updatedContact = await contactsResource.patch(contact.providerId, contact);
      setContactBeingEdited(undefined);
      onContactsUpdated(contacts.map((c) => (c.providerId === updatedContact.providerId ? updatedContact : c)));
      enqueueSnackbar('Contact saved.');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setAPIErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  }

  const handleCancelEditContact = () => {
    if (!contactBeingEdited) return;

    setContactBeingEdited(undefined);
  };

  /**
   * Copy the contact that is selected, stash it
   * @param contact selected contact
   */
  const handleEditContact = (contact: Contact) => {
    setContactBeingEdited({ ...contact });

    // Optimistic save button
    setIsSaving(false);
  };

  return (
    <Box>
      {contacts
        ?.filter((contact) => contact.providerId !== contactBeingEdited?.providerId)
        .map((contact) => {
          return (
            <ReadOnlyContact
              key={`readonly-contact-${contact.providerId}`}
              contact={contact}
              onEditContact={handleEditContact}
              onDeleteContact={handleDeleteContact}
              isDisabled={isSaving || isAddingContact || !!contactBeingEdited || disabledFromParent === true}
            />
          );
        })}
      {!isAddingContact && (
        <Chip
          icon={<AddIcon />}
          label='Add Contact'
          onClick={() => {
            setContactBeingAdded({ type: ST.ContactTypeEnum.MobilePhone, value: '' });
            setIsAddingContact(true); // If this is true, replace with save button!
          }}
          disabled={isAddingContact || disabledFromParent === true || !!contactBeingEdited}
        />
      )}
      {!!contactBeingEdited && (
        <EditableContact
          key={`edit-contact-${contactBeingEdited.providerId}`}
          contact={contactBeingEdited}
          onSaveContact={handleSaveContact}
          onCancelEditContact={handleCancelEditContact}
          isDisabled={isSaving || isAddingContact || disabledFromParent === true}
        />
      )}
      {isAddingContact && (
        <EditableContact
          contact={contactBeingAdded}
          onSaveContact={handleAddContact}
          onCancelEditContact={() => setIsAddingContact(false)}
          isDisabled={isSaving || disabledFromParent === true}
        />
      )}
    </Box>
  );
};
